import {
  ParlorHeadSection,
  ETypeMainHead,
  CTypeCardView,
  BTypeParlorList,
  FUNERAL_SIDO,
  DTypeBreadCrumb,
  FUNERAL_SIGUNGU,
  BTypeFlowerSummary,
  colors,
  excludeParlorList,
  HLine,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import parlors from "goi_common/src/constants/parlors_detail.json";

import React from "react";
import { ParlorDetailProps, numberToLocalString, BTypeTabs } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = parlors
    .filter((res) => targetSidos.includes(res.sido))
    .map((parlor) => ({
      params: {
        id: String(parlor.id),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id;
  let parlor;
  let district;
  let name = "";
  let description = "";

  if (id) {
    parlor = parlors.find((parlor) => parlor.id === Number(id));
    if (parlor) {
      name = parlor.companyname;

      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      const descParts = [
        `${parlor.companyname} 근조화환·조화 배송 안내입니다`,
        `고이장례연구소에서 e하늘 장사정보 기반으로 안내드립니다`,
        `주소 : ${parlor.address}`,
        `2시간 이내 당일 배송`,
        `삼가 고인의 명복을 빕니다`,
      ].filter(Boolean);
      description = descParts.join(' | ');
    }
  }

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords: `${parlor?.companyname}, ${parlor?.companyname} 화환, ${parlor?.companyname} 화환 배송, ${parlor?.companyname} 근조화환, ${parlor?.companyname} 꽃, ${parlor?.companyname} 꽃배달, ${parlor?.companyname} 꽃집, 꽃배달, 꽃, 꽃집, 화환`,
      pageOgTitle: `${parlor?.companyname} 근조화환·꽃배달 (${district}) | 서울경기인천 장례정보 [고이장례연구소]`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || "",
      hasError: !parlor,
    },
  };
};

export default function flower({
  defaultParlor,
  pageOgTitle,
}: {
  defaultParlor: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionParlors = parlors.filter((res) => targetSidos.includes(res.sido));
  const sidoName = FUNERAL_SIDO.get(Number(defaultParlor.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultParlor.sido))?.get(
    Number(defaultParlor.sigungu)
  );

  const carouselItems = regionParlors.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.metropolitan-funeral.co.kr/parlor-places/${res.id}/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionParlors.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  if (excludeParlorList.includes(String(defaultParlor.id))) {
    return (
      <>
        <Head>
          <title>{pageOgTitle}</title>
        </Head>
        <ParlorHeadSection
          component={
            <>
              <DTypeBreadCrumb
                mainColor={regionColor}
                firstItem={{
                  text: `${sidoName} 장례식장`,
                  href: `/price-funeral/${sidoName} 지역 장례식장 추천/`,
                  event: {
                    domain: "metro",
                    path: "prlr_flower",
                    trigger_and_target: "br_crumb_sido_prlr_list",
                  },
                }}
                secondItem={{
                  text: `${sigunguName} 장례식장`,
                  href: `/price-funeral/${sidoName} 지역-${sigunguName} 장례식장 추천/`,
                  event: {
                    domain: "metro",
                    path: "prlr_flower",
                    trigger_and_target: "br_crumb_sigungu_prlr_list",
                  },
                }}
                goiHomeEvent={{
                  domain: "metro",
                  path: "prlr_flower",
                  trigger_and_target: "br_crumb_goi_home",
                }}
              />
              <ETypeMainHead
                mainColor={regionColor}
                defaultParlor={defaultParlor}
                title={`${defaultParlor.companyname}`}
                managed_type={defaultParlor.managed_type}
                event={{
                  domain: "metro",
                  path: "prlr_flower",
                  trigger_and_target: "uv_mth_req",
                }}
              />
            </>
          }
        />
        <Stack>해당 장례식장 요청으로 정보를 공개하지 않습니다.</Stack>
      </>
    );
  }
  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        {regionParlors.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>
      <ParlorHeadSection
        component={
          <>
            <DTypeBreadCrumb
              mainColor={regionColor}
              firstItem={{
                text: `${sidoName} 장례식장`,
                href: `/price-funeral/${sidoName} 지역 장례식장 추천/`,
              }}
              secondItem={{
                text: `${sigunguName} 장례식장`,
                href: `/price-funeral/${sidoName} 지역-${sigunguName} 장례식장 추천/`,
              }}
            />
            <ETypeMainHead
              mainColor={regionColor}
              defaultParlor={defaultParlor}
              title={`${defaultParlor.companyname} 근조화환`}
              managed_type={defaultParlor.managed_type}
              event={{
                domain: "metro",
                path: "prlr_flower",
                trigger_and_target: "uv_mth_req_btom",
              }}
            />
            <Box padding="0 20px">
              <HLine height="1px" color={colors.gray900} />
            </Box>
            <Box width="100%" className="subtitle_14_sb gray900" padding="0 20px" marginTop="12px" textAlign="center">
              근조화환 배송 서비스 (고이장례연구소)
            </Box>
          </>
        }
      />

      <BTypeTabs
        mainColor={regionColor}
        href={`parlor-places/${defaultParlor.id}`}
        selectTab="화환 보내기"
        defaultParlor={defaultParlor}
        event={{
          domain: "metro",
          path: "prlr_flower",
          trigger_and_target: "BType_tab",
        }}
      />

      <Stack padding="20px 0 50px">
        <BTypeFlowerSummary
          defaultParlor={defaultParlor}
          mainColor={regionColor}
          event={{
            domain: "metro",
            path: "prlr_flower",
            trigger_and_target: "uv_wreath_req",
          }}
        />
      </Stack>
      <Box padding="0 20px">
        <Box
          width="100%"
          height="1px"
          style={{ background: colors.gray900 }}
          margin="0 0 20px"
          padding="0 20px"
        />
      </Box>
      <Stack padding="20px 0px">
        <BTypeParlorList
          sido={defaultParlor.sido}
          sigungu={String(defaultParlor.sigungu)}
          currentParlorId={defaultParlor.id}

          currentLat={Number((defaultParlor as any).latitude)}

          currentLng={Number((defaultParlor as any).longitude)}
          title={`${defaultParlor.companyname} 근처 장례식장`}
          CardViewType={(res) => (
            <CTypeCardView
              sigungu={res.sigungu}
              mainColor={regionColor}
              key={res.companyname}
              name={res.companyname}
              href={`/parlor-places/${res.id}/`}
              sido={res.sido}
              descriptions={res.distanceLabel ? [`${defaultParlor.companyname}에서 ${res.distanceLabel}`] : []}
              imgSrc={res.images[0] ? res.images[0].url : ""}
              event={{
                domain: "metro",
                path: "prlr_flower",
                trigger_and_target: "uv_prlr_detail",
              }}
            />
          )}
        />
      </Stack>
    </>
  );
}
